import { useVirtualizer as useTanStackVirtualizer } from "@tanstack/react-virtual";

/**
 * A wrapper hook around TanStack's useVirtualizer with sensible defaults
 * and browser-specific optimizations.
 *
 * @param {Object} options - Configuration options for the virtualizer
 * @param {number} [options.count=0] - Total number of items to render virtually
 * @param {Function} [options.estimateSize=() => 50] - Function to estimate the size of each item
 * @param {number} [options.overscan=5] - Number of items to render outside of the visible area
 * @param {React.RefObject} options.scrollRef - Ref object for the scrollable container element
 * @param {Object} [options.otherParams] - Additional parameters to pass to TanStack's useVirtualizer
 *
 * @returns {Object} TanStack virtualizer instance with methods for virtualized rendering
 *
 * @example
 * const scrollRef = useRef(null);
 * const rowVirtualizer = useVirtualizer({
 *   count: items.length,
 *   scrollRef,
 *   estimateSize: () => 60,
 * });
 */
const useVirtualizer = ({
  count = 0,
  estimateSize = () => 50,
  overscan = 5,
  scrollRef,
  ...otherParams
} = {}) => {
  return useTanStackVirtualizer({
    count,
    estimateSize,
    getScrollElement: () => scrollRef.current,
    overscan,
    ...otherParams,
  });
};

export default useVirtualizer;
